// fin-adapters / supabase / ledgerReader — the Supabase adapter of fin-core's LedgerReader.
//
// This is the anti-corruption layer: snake_case rows in, camelCase domain out, bigint cents checked to
// be safe integers, and every failure turned into a typed Result. Two rules matter more than the rest:
//   * `42501` (a missing right) is `forbidden`. It is NEVER an empty list.
//   * a row this build cannot read is an error, never a silently dropped row or a zero.
//
// Bound to ONE établissement at construction. The RPCs are admin-only (fin.ledger.read) and bounded.

import {
  err,
  ok,
  type CustomerBalance,
  type CustomerBalancePage,
  type CustomerBalanceQuery,
  type CustomerLedgerQuery,
  type CustomerMovement,
  type DocumentDetail,
  type DocumentEntry,
  type JournalLine,
  type JournalLinePage,
  type JournalLineQuery,
  type LedgerReader,
  type LedgerStatus,
  type Result,
  type TrialBalanceLine,
  type VegaInvoice,
  type VegaPaymentMovement,
  type BlockedDay,
  type Centralisation,
  type CentralisationLine,
} from '@lemontaigu/fin-core';
import type { SupabaseClient } from '@supabase/supabase-js';

export type { SupabaseClient };

/** A row (or a whole answer) that is not what the contract says. Caught at the method boundary. */
export class MalformedRow extends Error {}

// ── reading a value, strictly ───────────────────────────────────────────────

type Raw = Record<string, unknown>;

const isObject = (v: unknown): v is Raw => typeof v === 'object' && v !== null && !Array.isArray(v);

function obj(v: unknown, what: string): Raw {
  if (!isObject(v)) throw new MalformedRow(`${what}: objet attendu`);
  return v;
}
function arr(v: unknown, what: string): unknown[] {
  if (!Array.isArray(v)) throw new MalformedRow(`${what}: liste attendue`);
  return v;
}
function str(v: unknown, what: string): string {
  if (typeof v !== 'string') throw new MalformedRow(`${what}: texte attendu`);
  return v;
}
function strOrNull(v: unknown, what: string): string | null {
  if (v === null || v === undefined) return null;
  return str(v, what);
}
/** Cents (and counts) must be SAFE INTEGERS. A decimal or a huge number is a defect, not something to round. */
function int(v: unknown, what: string): number {
  if (typeof v !== 'number' || !Number.isSafeInteger(v)) throw new MalformedRow(`${what}: entier attendu`);
  return v;
}
function intOrNull(v: unknown, what: string): number | null {
  if (v === null || v === undefined) return null;
  return int(v, what);
}
function bool(v: unknown, what: string): boolean {
  if (typeof v !== 'boolean') throw new MalformedRow(`${what}: booléen attendu`);
  return v;
}

// ── mappers (exported: they are what the tests pin) ─────────────────────────

const blockedDays = (v: unknown, what: string): BlockedDay[] =>
  arr(v, what).map((d) => {
    const o = obj(d, what);
    return { day: str(o.day, `${what}.day`), blockers: arr(o.blockers, `${what}.blockers`).map((b) => str(b, `${what}.blockers`)) };
  });

export function toLedgerStatus(raw: unknown): LedgerStatus {
  const o = obj(raw, 'état du livre');
  const lastRun = o.last_run === null || o.last_run === undefined ? null : obj(o.last_run, 'last_run');
  const openRun = o.open_run === null || o.open_run === undefined ? null : obj(o.open_run, 'open_run');
  return {
    book: str(o.book, 'book'),
    property: str(o.property, 'property'),
    openedOn: str(o.opened_on, 'opened_on'),
    asOf: str(o.as_of, 'as_of'),
    postedThrough: str(o.posted_through, 'posted_through'),
    firstUnpostedDay: strOrNull(o.first_unposted_day, 'first_unposted_day'),
    postedDays: int(o.posted_days, 'posted_days'),
    quietDays: int(o.quiet_days, 'quiet_days'),
    addenda: int(o.addenda, 'addenda'),
    blocked: blockedDays(o.blocked, 'blocked'),
    drift: arr(o.drift, 'drift').map((d) => {
      const x = obj(d, 'drift');
      return { day: str(x.day, 'drift.day'), kind: str(x.kind, 'drift.kind'), sourceId: str(x.source_id, 'drift.source_id') };
    }),
    lastEventAt: strOrNull(o.last_event_at, 'last_event_at'),
    lastRun: lastRun && {
      id: int(lastRun.id, 'last_run.id'),
      finishedAt: str(lastRun.finished_at, 'last_run.finished_at'),
      outcome: str(lastRun.outcome, 'last_run.outcome'),
      daysPosted: int(lastRun.days_posted, 'last_run.days_posted'),
      daysBlocked: blockedDays(lastRun.days_blocked, 'last_run.days_blocked'),
      driftCount: int(lastRun.drift_count, 'last_run.drift_count'),
    },
    openRun: openRun && { id: int(openRun.id, 'open_run.id'), startedAt: str(openRun.started_at, 'open_run.started_at') },
    neverRun: bool(o.never_run, 'never_run'),
    silentMinutes: intOrNull(o.silent_minutes, 'silent_minutes'),
    posterSilent: bool(o.poster_silent, 'poster_silent'),
    openMonths: arr(o.open_months, 'open_months').map((m) => str(m, 'open_months')),
  };
}

export function toTrialBalanceLine(raw: unknown): TrialBalanceLine {
  const o = obj(raw, 'ligne de balance');
  return {
    account: str(o.account, 'account'),
    label: str(o.label, 'label'),
    debitCents: int(o.debit_cents, 'debit_cents'),
    creditCents: int(o.credit_cents, 'credit_cents'),
    netCents: int(o.net_cents, 'net_cents'),
  };
}

export function toCustomerBalance(raw: unknown): CustomerBalance {
  const o = obj(raw, 'solde client');
  return {
    holderRef: str(o.holder_ref, 'holder_ref'),
    holderName: strOrNull(o.holder_name, 'holder_name'),
    holderKind: str(o.holder_kind, 'holder_kind'),
    dueCents: int(o.due_cents, 'due_cents'),
    lastMovement: strOrNull(o.last_movement, 'last_movement'),
    lineCount: int(o.line_count, 'line_count'),
    since: str(o.since, 'since'),
  };
}

export function toCustomerMovement(raw: unknown): CustomerMovement {
  const o = obj(raw, 'mouvement client');
  return {
    entryId: int(o.entry_id, 'entry_id'),
    entryDate: str(o.entry_date, 'entry_date'),
    journal: str(o.journal, 'journal'),
    pieceRef: str(o.piece_ref, 'piece_ref'),
    label: strOrNull(o.label, 'label'),
    debitCents: int(o.debit_cents, 'debit_cents'),
    creditCents: int(o.credit_cents, 'credit_cents'),
    runningDueCents: int(o.running_due_cents, 'running_due_cents'),
    sourceKind: str(o.source_kind, 'source_kind'),
  };
}

export function toJournalLine(raw: unknown): JournalLine {
  const o = obj(raw, 'ligne du journal');
  return {
    lineId: int(o.line_id, 'line_id'),
    entryId: int(o.entry_id, 'entry_id'),
    entryDate: str(o.entry_date, 'entry_date'),
    journal: str(o.journal, 'journal'),
    pieceRef: str(o.piece_ref, 'piece_ref'),
    sourceKind: str(o.source_kind, 'source_kind'),
    account: str(o.account, 'account'),
    accountLabel: str(o.account_label, 'account_label'),
    holderRef: strOrNull(o.holder_ref, 'holder_ref'),
    debitCents: int(o.debit_cents, 'debit_cents'),
    creditCents: int(o.credit_cents, 'credit_cents'),
    label: strOrNull(o.label, 'label'),
  };
}

function toVegaInvoice(raw: unknown): VegaInvoice {
  const o = obj(raw, 'facture Vega');
  return {
    facture: str(o.facture, 'facture'),
    invoiceDate: strOrNull(o.invoice_date, 'invoice_date'),
    name: strOrNull(o.name, 'name'),
    clientRef: strOrNull(o.client_ref, 'client_ref'),
    totalCents: intOrNull(o.total_cents, 'total_cents'),
    settledCents: intOrNull(o.settled_cents, 'settled_cents'),
    depositAppliedCents: intOrNull(o.deposit_applied_cents, 'deposit_applied_cents'),
    base55Cents: intOrNull(o.base_5_5_cents, 'base_5_5_cents'),
    base10Cents: intOrNull(o.base_10_cents, 'base_10_cents'),
    base20Cents: intOrNull(o.base_20_cents, 'base_20_cents'),
    baseExemptCents: intOrNull(o.base_exempt_cents, 'base_exempt_cents'),
  };
}

function toDocumentEntry(raw: unknown): DocumentEntry {
  const o = obj(raw, 'écriture');
  return {
    entryId: int(o.entry_id, 'entry_id'),
    entryDate: str(o.entry_date, 'entry_date'),
    journal: str(o.journal, 'journal'),
    sourceKind: str(o.source_kind, 'source_kind'),
    sourceId: str(o.source_id, 'source_id'),
    lines: arr(o.lines, 'lines').map((l) => {
      const x = obj(l, 'ligne');
      return {
        lineNo: int(x.line_no, 'line_no'),
        account: str(x.account, 'account'),
        accountLabel: str(x.account_label, 'account_label'),
        holderRef: strOrNull(x.holder_ref, 'holder_ref'),
        debitCents: int(x.debit_cents, 'debit_cents'),
        creditCents: int(x.credit_cents, 'credit_cents'),
        label: strOrNull(x.label, 'label'),
      };
    }),
  };
}

function toVegaPayment(raw: unknown): VegaPaymentMovement {
  const o = obj(raw, 'mouvement Vega');
  return {
    kind: str(o.kind, 'kind'),
    kindLabel: strOrNull(o.kind_label, 'kind_label'),
    reference: strOrNull(o.reference, 'reference'),
    movementDate: strOrNull(o.movement_date, 'movement_date'),
    method: strOrNull(o.method, 'method'),
    amountCents: intOrNull(o.amount_cents, 'amount_cents'),
    name: strOrNull(o.name, 'name'),
    label: strOrNull(o.label, 'label'),
    writtenAt: strOrNull(o.written_at, 'written_at'),
  };
}

export function toDocumentDetail(raw: unknown): DocumentDetail {
  const o = obj(raw, 'pièce');
  return {
    piece: str(o.piece, 'piece'),
    invoice: o.invoice === null || o.invoice === undefined ? null : toVegaInvoice(o.invoice),
    entries: arr(o.entries, 'entries').map(toDocumentEntry),
    entriesTotal: int(o.entries_total, 'entries_total'),
    vegaPayments: arr(o.vega_payments, 'vega_payments').map(toVegaPayment),
  };
}

/** C5: a month's latest centralisation, or null when the month was never centralised. */
export function toCentralisation(raw: unknown): Centralisation | null {
  const o = obj(raw, 'centralisation');
  if (o.centralisation === null || o.centralisation === undefined) return null;
  const c = obj(o.centralisation, 'centralisation');
  return {
    id: int(c.id, 'centralisation.id'),
    version: int(c.version, 'centralisation.version'),
    periodStart: str(c.period_start, 'period_start'),
    periodEnd: str(c.period_end, 'period_end'),
    issuedAt: str(c.issued_at, 'issued_at'),
    sealed: bool(c.sealed, 'sealed'),
    lines: arr(o.lines, 'lignes').map((x): CentralisationLine => {
      const l = obj(x, 'ligne de centralisation');
      return {
        lineNo: int(l.line_no, 'line_no'),
        targetJournal: str(l.target_journal, 'target_journal'),
        piece: str(l.piece, 'piece'),
        entryDate: str(l.entry_date, 'entry_date'),
        account: str(l.account, 'account'),
        accountLabel: strOrNull(l.account_label, 'account_label'),
        debitCents: int(l.debit_cents, 'debit_cents'),
        creditCents: int(l.credit_cents, 'credit_cents'),
        label: str(l.label, 'label'),
        kind: str(l.kind, 'kind'),
      };
    }),
  };
}

// ── the error boundary ──────────────────────────────────────────────────────

interface RpcError {
  readonly code?: string | null;
  readonly message: string;
}

/** A missing right is `forbidden`, a transport fault is retryable, everything else is unknown. */
export function toFinError(e: RpcError): Result<never> {
  if (e.code === '42501') return err('forbidden', 'Droit manquant');
  if (/^(08|57)/.test(e.code ?? '')) return err('transient', e.message);
  return err('unknown', e.message);
}

// ── the adapter ─────────────────────────────────────────────────────────────

export class SupabaseLedgerReader implements LedgerReader {
  constructor(
    private readonly db: SupabaseClient,
    private readonly propertyCode: string,
  ) {}

  private async call<T>(fn: string, args: Record<string, unknown>, map: (data: unknown) => T): Promise<Result<T>> {
    const r = await this.db.rpc(fn, args);
    if (r.error) return toFinError(r.error);
    try {
      return ok(map(r.data));
    } catch (e) {
      if (e instanceof MalformedRow) return err('unknown', `Réponse illisible (${e.message})`, false);
      throw e;
    }
  }

  status(book: string): Promise<Result<LedgerStatus>> {
    return this.call('fin_ledger_status', { p_book: book, p_property: this.propertyCode }, toLedgerStatus);
  }

  trialBalance(book: string, from: string, to: string): Promise<Result<readonly TrialBalanceLine[]>> {
    return this.call('fin_trial_balance', { p_book: book, p_from: from, p_to: to }, (d) => arr(d, 'balance').map(toTrialBalanceLine));
  }

  customerBalances(book: string, query: CustomerBalanceQuery = {}): Promise<Result<CustomerBalancePage>> {
    return this.call(
      'fin_customer_balance',
      {
        p_book: book,
        p_property: this.propertyCode,
        p_as_of: query.asOf ?? null,
        p_include_settled: query.includeSettled ?? false,
        p_limit: query.limit ?? 500,
        p_after: query.after ?? null,
      },
      (d) => {
        const rows = arr(d, 'soldes clients');
        const total = rows.length === 0 ? 0 : int(obj(rows[0], 'solde client').total_holders, 'total_holders');
        return { rows: rows.map(toCustomerBalance), totalHolders: total };
      },
    );
  }

  customerLedger(book: string, holderRef: string, query: CustomerLedgerQuery = {}): Promise<Result<readonly CustomerMovement[]>> {
    return this.call(
      'fin_customer_ledger',
      { p_book: book, p_property: this.propertyCode, p_holder_ref: holderRef, p_limit: query.limit ?? 500, p_before: query.before ?? null },
      (d) => arr(d, 'mouvements').map(toCustomerMovement),
    );
  }

  journalLines(book: string, query: JournalLineQuery): Promise<Result<JournalLinePage>> {
    return this.call(
      'fin_journal_entries',
      {
        p_book: book,
        p_property: this.propertyCode,
        p_from: query.from,
        p_to: query.to,
        p_account: query.account ?? null,
        p_holder_ref: query.holderRef ?? null,
        p_limit: query.limit ?? 200,
        p_before: query.before ?? null,
      },
      (d) => {
        const rows = arr(d, 'lignes');
        const total = rows.length === 0 ? 0 : int(obj(rows[0], 'ligne').total_lines, 'total_lines');
        return { rows: rows.map(toJournalLine), totalLines: total };
      },
    );
  }

  document(book: string, piece: string): Promise<Result<DocumentDetail>> {
    return this.call('fin_document_detail', { p_book: book, p_property: this.propertyCode, p_piece: piece }, toDocumentDetail);
  }

  centralisation(book: string, month: string): Promise<Result<Centralisation | null>> {
    return this.call('fin_centralisation_read', { p_book: book, p_property: this.propertyCode, p_month: month }, toCentralisation);
  }
}
